import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { SessionDetailResumeStartedEventProps } from '@/models/analytics/features/sessions';
import {
  logHardwareDatadogEvent,
  logSessionPipelineDatadogEvent,
  type HardwareDatadogAttributes,
} from '@/utils/analytics/log-hardware-datadog-event';

/**
 * Session-detail resume funnel event (HAR-1334), shared with iOS/Android.
 *
 * Routing: Snowplow-first — `amplitude: false` is mandatory for new events;
 * `posthog: true` matches the Heidi-Remote family opt-in so the Remote
 * dashboards see it. Mirrored to the Datadog log index because the
 * cross-platform session-pipeline dashboards join on logs, where iOS's
 * Datadog sink also lands. The mirror branches by capture device: Chronicle
 * resumes keep the hardware sink (device identity belongs on them); mic
 * resumes use the shared session-pipeline sink so they don't carry Chronicle
 * device identity or the module:chronicle / hardware_datadog_only markers.
 */
export function trackSessionDetailResumeStarted(
  props: SessionDetailResumeStartedEventProps
): void {
  track({
    event_name: TrackingEventName.SessionDetailResumeStarted,
    props,
    options: { posthog: true, amplitude: false },
  });
  /* Typed literal (no cast) so any future field must satisfy the Datadog
     attribute type before it can ship to the log index. */
  const attributes: HardwareDatadogAttributes = {
    session_id: props.session_id,
    session_id_local: props.session_id_local,
    previous_duration: props.previous_duration,
    recording_device: props.recording_device,
  };
  if (props.recording_device === 'remote') {
    logHardwareDatadogEvent(TrackingEventName.SessionDetailResumeStarted, attributes);
  } else {
    logSessionPipelineDatadogEvent(
      TrackingEventName.SessionDetailResumeStarted,
      attributes
    );
  }
}
